// src/modules/app-views/crm/pages/Boards/modals/BoardSettingsModal/tabs/ColumnsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Button, Card, Input, Switch, Tag, Tooltip } from 'antd';
import SimpleColorPicker from 'components/SimpleComponents/SimpleColorPicker';
import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import translateX from 'utils/translateX';
import { generateUidV4 } from 'utils/helpers';
import { generateHexColor } from 'utils/colors';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ColumnsTab = ({
  localColumns,
  setLocalColumns,
  fixedColumns,
  setFixedColumns,
  allowNewColumns,
  setAllowNewColumns,
  allowColumnEdit,
  setAllowColumnEdit,
  allowMoveCards,
  setAllowMoveCards,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Separate fixed columns (initial/final) from draggable middle columns
  const middleColumns = localColumns.filter(c => !c.initial && !c.final);
  const initialColumn = localColumns.find(c => c.initial);
  const finalColumn = localColumns.find(c => c.final);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = middleColumns.findIndex(c => c.uid === active.id);
    const newIndex = middleColumns.findIndex(c => c.uid === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(middleColumns, oldIndex, newIndex);
    // Rebuild full array: initial + reordered middle + final
    setLocalColumns([
      ...(initialColumn ? [initialColumn] : []),
      ...reordered,
      ...(finalColumn ? [finalColumn] : []),
    ]);
  };

  const handleTitleChange = (uid, value) => {
    setLocalColumns(prev => prev.map(c => (c.uid === uid ? { ...c, title: value } : c)));
  };

  const handleDescriptionChange = (uid, value) => {
    setLocalColumns(prev => prev.map(c => (c.uid === uid ? { ...c, description: value } : c)));
  };

  const handleColorChange = (uid, hex) => {
    setLocalColumns(prev => prev.map(c => (c.uid === uid ? { ...c, color: hex } : c)));
  };

  const handleDelete = uid => {
    setLocalColumns(prev => prev.filter(c => c.uid !== uid));
  };

  const handleAddColumn = () => {
    setLocalColumns(prev => {
      // Insert new column before the final column
      const finalIdx = prev.findIndex(c => c.final);
      const newCol = {
        uid: generateUidV4(),
        title: `${translateX('column')} ${prev.length}`,
        color: generateHexColor(),
        order: prev.length + 1,
      };
      if (finalIdx >= 0) {
        const copy = [...prev];
        copy.splice(finalIdx, 0, newCol);
        return copy;
      }
      return [...prev, newCol];
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingRight: 4, paddingBottom: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 12 }}>
        {/* Initial column (fixed) */}
        {initialColumn && (
          <FixedColumnRow
            col={initialColumn}
            tagLabel={translateX('initial')}
            tagColor="blue"
            onTitleChange={handleTitleChange}
            onDescriptionChange={handleDescriptionChange}
            onColorChange={handleColorChange}
          />
        )}

        {/* Middle columns (draggable) */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={middleColumns.map(c => c.uid)}
            strategy={verticalListSortingStrategy}
          >
            {middleColumns.map(col => (
              <SortableColumnRow
                key={col.uid}
                col={col}
                onTitleChange={handleTitleChange}
                onDescriptionChange={handleDescriptionChange}
                onColorChange={handleColorChange}
                onDelete={handleDelete}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Add column button — inserts before final column */}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          size="small"
          onClick={handleAddColumn}
          style={{ marginTop: 4 }}
        >
          {translateX('add_column')}
        </Button>

        {/* Final column (fixed) */}
        {finalColumn && (
          <div style={{ marginTop: 4 }}>
            <FixedColumnRow
              col={finalColumn}
              tagLabel={translateX('final')}
              tagColor="red"
              onTitleChange={handleTitleChange}
              onDescriptionChange={handleDescriptionChange}
              onColorChange={handleColorChange}
            />
          </div>
        )}

        {/* Permissions card */}
        <Card
          size="small"
          title={translateX('permissions')}
          style={{ marginTop: 12 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{translateX('keep_columns_fixed')}</span>
              <Switch checked={fixedColumns} onChange={setFixedColumns} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{translateX('allow_new_columns')}</span>
              <Switch checked={allowNewColumns} onChange={setAllowNewColumns} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{translateX('edit_column')}</span>
              <Switch checked={allowColumnEdit} onChange={setAllowColumnEdit} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{translateX('move_cards_between_columns')}</span>
              <Switch checked={allowMoveCards} onChange={setAllowMoveCards} />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ColumnsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Fixed column row (initial or final) — no drag, no delete
function FixedColumnRow({ col, tagLabel, tagColor, onTitleChange, onDescriptionChange, onColorChange }) {
  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <HolderOutlined style={{ fontSize: 14, opacity: 0.2, cursor: 'not-allowed', flexShrink: 0 }} />

        <Input
          value={col.title}
          onChange={e => onTitleChange(col.uid, e.target.value)}
          style={{ flex: 1, height: 40 }}
          suffix={
            <Tag color={tagColor} style={{ margin: 0, fontSize: 11 }}>
              {tagLabel}
            </Tag>
          }
        />

        <SimpleColorPicker
          value={col.color}
          onChange={color => onColorChange(col.uid, color.toHexString())}
        />

        <Tooltip title={undefined}>
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            disabled
            style={{ padding: '0 4px', flexShrink: 0 }}
          />
        </Tooltip>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 22 }}>
        <Input
          value={col.description || ''}
          placeholder={translateX('column_description')}
          onChange={e => onDescriptionChange(col.uid, e.target.value)}
          style={{ flex: 1, height: 32, marginRight: 12, fontSize: 11, color: '#8c8c8c' }}
        />
        <div style={{ width: 40, flexShrink: 0 }} />
        <div style={{ width: 24, flexShrink: 0 }} />
      </div>
    </div>
  );
}

// Draggable column row (middle columns) — can drag, can delete
function SortableColumnRow({ col, onTitleChange, onDescriptionChange, onColorChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.uid,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    padding: '4px 0',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <HolderOutlined
          {...attributes}
          {...listeners}
          style={{ fontSize: 14, cursor: 'grab', opacity: 0.45, flexShrink: 0 }}
        />

        <Input
          value={col.title}
          onChange={e => onTitleChange(col.uid, e.target.value)}
          style={{ flex: 1, height: 40 }}
        />

        <SimpleColorPicker
          value={col.color}
          onChange={color => onColorChange(col.uid, color.toHexString())}
        />

        <Tooltip title={translateX('delete')}>
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => onDelete(col.uid)}
            style={{ padding: '0 4px', flexShrink: 0 }}
          />
        </Tooltip>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 22 }}>
        <Input
          value={col.description || ''}
          placeholder={translateX('column_description')}
          onChange={e => onDescriptionChange(col.uid, e.target.value)}
          style={{ flex: 1, height: 32, marginRight: 12, fontSize: 11, color: '#8c8c8c' }}
        />
        <div style={{ width: 40, flexShrink: 0 }} />
        <div style={{ width: 24, flexShrink: 0 }} />
      </div>
    </div>
  );
}
